<template>
  <div
    class="ncrm-activity-list-card"
    :class="{ 'ncrm-activity-list-card--closed': isClosed }"
    :style="{ borderLeftColor: accentColor }"
    role="button"
    tabindex="0"
    @click="$emit('open')"
    @keydown.enter.prevent="$emit('open')"
    @keydown.space.prevent="$emit('open')"
  >
    <div class="ncrm-activity-list-card__head">
      <div class="ncrm-activity-list-card__entity">{{ entityName || "-" }}</div>
      <span v-if="overdue" class="ncrm-activity-list-card__overdue-tag">{{ overdueLabel }}</span>
    </div>

    <div class="ncrm-activity-list-card__context">
      <v-icon size="13" :color="contextColor">{{ contextIcon }}</v-icon>
      <span class="ncrm-activity-list-card__context-text">
        {{ contextLabel }}<template v-if="title"> — {{ title }}</template>
      </span>
    </div>

    <div class="ncrm-activity-list-card__meta">
      <div class="ncrm-activity-list-card__chips">
        <v-chip size="x-small" color="primary">{{ typeLabel }}</v-chip>
        <v-chip size="x-small" :color="statusColor">{{ statusLabel }}</v-chip>
      </div>
      <span
        class="ncrm-activity-list-card__due"
        :class="{ 'ncrm-activity-list-card__due--overdue': overdue }"
      >
        {{ dueText }}
      </span>
    </div>
  </div>
</template>

<script>
const THEME_ACCENT_KEYS = new Set(["primary", "secondary", "tertiary", "quaternary", "success", "warning", "error", "info"]);

export default {
  name: "NcrmActivityListCard",
  props: {
    entityName: { type: String, default: "" },
    title: { type: String, default: "" },
    contextLabel: { type: String, default: "" },
    contextIcon: { type: String, default: "mdi-shape-outline" },
    contextColor: { type: String, default: "primary" },
    typeLabel: { type: String, default: "" },
    statusLabel: { type: String, default: "" },
    statusColor: { type: String, default: "primary" },
    dueText: { type: String, default: "" },
    overdue: { type: Boolean, default: false },
    overdueLabel: { type: String, default: "" },
    accentColorKey: { type: String, default: "primary" },
    isClosed: { type: Boolean, default: false },
  },
  emits: ["open"],
  computed: {
    accentColor() {
      const key = THEME_ACCENT_KEYS.has(this.accentColorKey) ? this.accentColorKey : "primary";
      return `rgb(var(--v-theme-${key}))`;
    },
  },
};
</script>

<style scoped>
.ncrm-activity-list-card {
  background: rgb(var(--v-theme-surface));
  border: 1px solid var(--ncrm-color-border-default);
  border-left: 3px solid rgb(var(--v-theme-primary));
  border-radius: var(--app-radius-base);
  box-shadow: var(--app-shadow-sm);
  padding: 12px 14px;
  cursor: pointer;
  transition: box-shadow 150ms ease;
}

.ncrm-activity-list-card:hover,
.ncrm-activity-list-card:focus-visible {
  box-shadow: var(--app-shadow-md);
  outline: none;
}

.ncrm-activity-list-card--closed {
  opacity: 0.72;
}

.ncrm-activity-list-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.ncrm-activity-list-card__entity {
  min-width: 0;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.35;
  color: var(--ncrm-color-text-strong);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ncrm-activity-list-card--closed .ncrm-activity-list-card__entity {
  text-decoration: line-through;
  text-decoration-color: var(--ncrm-color-border-strong);
}

.ncrm-activity-list-card__overdue-tag {
  flex-shrink: 0;
  white-space: nowrap;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.4;
  padding: 2px 8px;
  border-radius: var(--app-radius-pill);
  background-color: rgba(var(--v-theme-error), 0.12);
  color: rgb(var(--v-theme-error));
}

.ncrm-activity-list-card__context {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 2px;
  min-width: 0;
  font-size: 13px;
  line-height: 1.45;
  color: rgba(var(--v-theme-on-surface), 0.55);
}

.ncrm-activity-list-card__context-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ncrm-activity-list-card__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 10px;
}

.ncrm-activity-list-card__chips {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.ncrm-activity-list-card__due {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--ncrm-color-text-strong);
}

.ncrm-activity-list-card__due--overdue {
  color: rgb(var(--v-theme-error));
}
</style>
